import React, { useState, useEffect, useMemo } from 'react';
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Mail, Trophy, Zap, Target, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicLeaderboard = ({ onLogin }) => {
  const [allRecords, setAllRecords] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState("ten_yard_sprint");
  const [aesLeaderboardData, setAesLeaderboardData] = useState([]);
  const [aesLeaderboardByAge, setAesLeaderboardByAge] = useState({});

  useEffect(() => {
    fetchPublicRecords();
    fetchAESLeaderboard();
  }, []);

  const fetchAESLeaderboard = async () => {
    try {
      const [aesResponse, aesByAgeResponse] = await Promise.all([
        axios.get(`${API}/leaderboard/aes`),
        axios.get(`${API}/leaderboard/aes/by-age`)
      ]);
      
      setAesLeaderboardData(aesResponse.data.leaderboard || []);
      setAesLeaderboardByAge(aesByAgeResponse.data.leaderboards || {});
      console.log('AES Leaderboard loaded:', aesResponse.data.leaderboard?.length, 'athletes');
      console.log('Total qualified for AES:', aesResponse.data.total_qualified);
    } catch (error) {
      console.error("Failed to fetch AES leaderboard:", error);
    }
  };

  const fetchPublicRecords = async () => {
    try {
      const response = await axios.get(`${API}/athletes`);
      const athletesList = response.data;
      setAthletes(athletesList);
      
      let allRecordsData = [];
      for (const athlete of athletesList) {
        try {
          const recordsResponse = await axios.get(`${API}/records/athlete/${athlete.id}`);
          if (recordsResponse.data && recordsResponse.data.length > 0) {
            const athleteRecords = recordsResponse.data.map(record => ({
              ...record,
              athlete_name: athlete.name,
              athlete_age: record.athlete_age || athlete.age
            }));
            allRecordsData = allRecordsData.concat(athleteRecords);
          }
        } catch (error) {
          console.error(`Failed to fetch records for ${athlete.name}:`, error);
        }
      }
      console.log('Total records loaded:', allRecordsData.length);
      console.log('Records with all AES metrics:', allRecordsData.filter(r => 
        r.ten_yard_sprint !== null && r.ten_yard_sprint !== undefined &&
        r.broad_jump !== null && r.broad_jump !== undefined &&
        r.vertical_jump !== null && r.vertical_jump !== undefined
      ).length);
      setAllRecords(allRecordsData);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch public records:", error);
      setLoading(false);
    }
  };

  const getLeaderboard = (metric, sortOrder = 'asc') => {
    const recordsWithMetric = allRecords.filter(r => r[metric] !== null && r[metric] !== undefined);
    
    const athleteBestMap = {};
    recordsWithMetric.forEach(record => {
      const athleteName = record.athlete_name;
      if (!athleteBestMap[athleteName]) {
        athleteBestMap[athleteName] = {
          value: record[metric],
          date: record.date,
          athlete_name: athleteName
        };
      } else {
        if (sortOrder === 'asc') {
          if (record[metric] < athleteBestMap[athleteName].value) {
            athleteBestMap[athleteName] = {
              value: record[metric],
              date: record.date,
              athlete_name: athleteName
            };
          }
        } else {
          if (record[metric] > athleteBestMap[athleteName].value) {
            athleteBestMap[athleteName] = {
              value: record[metric],
              date: record.date,
              athlete_name: athleteName
            };
          }
        }
      }
    });
    
    const leaderboard = Object.values(athleteBestMap);
    leaderboard.sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.value - b.value;
      } else {
        return b.value - a.value;
      }
    });
    
    return leaderboard.slice(0, 10);
  };

  // Calculate Armory Explosiveness Score (AES)
  // AES = 50 * (2 / 10y) + 30 * (BJ / 60) + 20 * (VJ / 14)
  const calculateAES = (tenYardSprint, broadJump, verticalJump) => {
    if (tenYardSprint === null || tenYardSprint === undefined || 
        broadJump === null || broadJump === undefined || 
        verticalJump === null || verticalJump === undefined) {
      return null;
    }
    
    const speedComponent = 50 * (2 / tenYardSprint);
    const broadJumpComponent = 30 * (broadJump / 60);
    const verticalJumpComponent = 20 * (verticalJump / 14);
    
    const aes = speedComponent + broadJumpComponent + verticalJumpComponent;
    return Math.round(aes * 100) / 100;
  };

  // Get AES leaderboard (overall top 10) - memoized to recalculate when allRecords changes
  const aesLeaderboard = useMemo(() => {
    const athleteAESMap = {};
    
    allRecords.forEach(record => {
      const aes = calculateAES(record.ten_yard_sprint, record.broad_jump, record.vertical_jump);
      if (aes !== null) {
        const athleteName = record.athlete_name;
        if (!athleteAESMap[athleteName] || aes > athleteAESMap[athleteName].value) {
          athleteAESMap[athleteName] = {
            value: aes,
            athlete_name: athleteName
          };
        }
      }
    });
    
    const leaderboard = Object.values(athleteAESMap);
    leaderboard.sort((a, b) => b.value - a.value);
    return leaderboard.slice(0, 10);
  }, [allRecords]);

  const getAESLeaderboard = () => aesLeaderboard;

  // Get AES leaderboard by age
  const getAESLeaderboardByAge = () => {
    const ageGroups = {};
    
    allRecords.forEach(record => {
      const aes = calculateAES(record.ten_yard_sprint, record.broad_jump, record.vertical_jump);
      if (aes !== null) {
        let age = record.athlete_age;
        if (age === null || age === undefined) {
          const athlete = athletes.find(a => a.name === record.athlete_name);
          if (athlete) {
            age = athlete.age;
          }
        }
        
        if (age !== null && age !== undefined) {
          if (!ageGroups[age]) {
            ageGroups[age] = {};
          }
          
          const athleteName = record.athlete_name;
          if (!ageGroups[age][athleteName] || aes > ageGroups[age][athleteName].value) {
            ageGroups[age][athleteName] = {
              value: aes,
              athlete_name: athleteName,
              athlete_age: age
            };
          }
        }
      }
    });
    
    const ageLeaderboards = {};
    Object.keys(ageGroups).forEach(age => {
      const leaderboard = Object.values(ageGroups[age]);
      leaderboard.sort((a, b) => b.value - a.value);
      ageLeaderboards[age] = leaderboard.slice(0, 10);
    });
    
    return ageLeaderboards;
  };

  const getLeaderboardByAge = (metric, sortOrder = 'asc') => {
    const ageGroups = {};
    
    allRecords.forEach(record => {
      if (record[metric] !== null && record[metric] !== undefined) {
        // Use athlete_age from the record (age when record was set)
        // Fall back to current athlete age for backwards compatibility
        let age = record.athlete_age;
        if (age === null || age === undefined) {
          const athlete = athletes.find(a => a.name === record.athlete_name);
          if (athlete) {
            age = athlete.age;
          }
        }
        
        if (age !== null && age !== undefined) {
          if (!ageGroups[age]) {
            ageGroups[age] = [];
          }
          ageGroups[age].push({
            athlete_name: record.athlete_name,
            value: record[metric],
            date: record.date,
            athlete_age: age
          });
        }
      }
    });
    
    const ageLeaderboards = {};
    Object.keys(ageGroups).forEach(age => {
      const athleteBestMap = {};
      
      ageGroups[age].forEach(record => {
        const athleteName = record.athlete_name;
        if (!athleteBestMap[athleteName]) {
          athleteBestMap[athleteName] = record;
        } else {
          if (sortOrder === 'asc') {
            if (record.value < athleteBestMap[athleteName].value) {
              athleteBestMap[athleteName] = record;
            }
          } else {
            if (record.value > athleteBestMap[athleteName].value) {
              athleteBestMap[athleteName] = record;
            }
          }
        }
      });
      
      const leaderboard = Object.values(athleteBestMap);
      leaderboard.sort((a, b) => {
        if (sortOrder === 'asc') {
          return a.value - b.value;
        } else {
          return b.value - a.value;
        }
      });
      
      ageLeaderboards[age] = leaderboard.slice(0, 10);
    });
    
    return ageLeaderboards;
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginLoading(true);

    if (password === "Armory") {
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 365);
      
      localStorage.setItem("armory_authenticated", "true");
      localStorage.setItem("armory_auth_expiry", expirationDate.toISOString());
      
      toast.success("Welcome to The Armory!");
      onLogin();
    } else {
      toast.error("Password Incorrect");
      setPassword("");
    }
    
    setLoginLoading(false);
  };

  const handlePasswordRecovery = async () => {
    setRecoveryLoading(true);
    try {
      await axios.post(`${API}/auth/password-recovery`);
      toast.success("Password recovery email sent to kta.jason1@gmail.com");
      setShowRecovery(false);
    } catch (error) {
      toast.error("Failed to send recovery email");
    } finally {
      setRecoveryLoading(false);
    }
  };

  const metrics = [
    { key: 'ten_yard_sprint', label: '10 Yard Sprint', sortOrder: 'asc', unit: 's', color: 'purple' },
    { key: 'ten_yard_fly', label: '10 Yard Fly', sortOrder: 'asc', unit: 's', color: 'purple' },
    { key: 'agility_505', label: '505 Agility', sortOrder: 'asc', unit: 's', color: 'orange' },
    { key: 'broad_jump', label: 'Broad Jump', sortOrder: 'desc', unit: '"', color: 'blue' },
    { key: 'vertical_jump', label: 'Vertical Jump', sortOrder: 'desc', unit: '"', color: 'blue' },
    { key: 'aes', label: 'AES', sortOrder: 'desc', unit: '', color: 'amber', isAES: true }
  ];

  // AES Leaderboard Card Component - uses backend-calculated data
  const AESLeaderboardCard = ({ title, color, icon: Icon }) => {
    const data = aesLeaderboardData;
    const colorClasses = {
      amber: { bg: 'bg-gradient-to-r from-amber-50 to-orange-50', border: 'border-amber-500', title: 'text-amber-700', badge: 'text-amber-600' }
    };
    const classes = colorClasses[color] || colorClasses.amber;

    return (
      <Card className="bg-white/95 backdrop-blur shadow-lg hover:shadow-xl transition-shadow" data-testid="leaderboard-aes">
        <CardHeader className={`${classes.bg} border-b-2 ${classes.border} py-3`}>
          <CardTitle className={`${classes.title} text-lg flex items-center gap-2`}>
            <Icon size={20} />
            {title}
          </CardTitle>
          <CardDescription className="text-xs">Armory Explosive Score (10yd Sprint, BJ, VJ)</CardDescription>
        </CardHeader>
        <CardContent className="pt-3 pb-2">
          <div className="space-y-1">
            {data.length > 0 ? data.map((entry, index) => (
              <div 
                key={`aes-${index}`} 
                className={`flex justify-between items-center py-1.5 px-2 rounded text-sm ${index < 3 ? 'bg-amber-50' : ''}`}
              >
                <span className="flex items-center gap-1.5">
                  {index === 0 && <Trophy size={12} className="text-yellow-500" />}
                  {index === 1 && <Trophy size={12} className="text-gray-400" />}
                  {index === 2 && <Trophy size={12} className="text-amber-600" />}
                  <span className={`font-bold ${classes.badge}`}>{index + 1}.</span> 
                  <span className="truncate max-w-[150px]">{entry.athlete_name}</span>
                </span>
                <span className={`font-bold ${classes.title} text-base`}>{entry.aes}</span>
              </div>
            )) : (
              <p className="text-sm text-gray-500 italic text-center py-4">No complete records yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const LeaderboardCard = ({ title, metric, sortOrder, color, icon: Icon, unit }) => {
    const data = getLeaderboard(metric, sortOrder);
    const colorClasses = {
      purple: { bg: 'bg-purple-50', border: 'border-purple-500', title: 'text-purple-700', badge: 'text-purple-600' },
      blue: { bg: 'bg-blue-50', border: 'border-blue-500', title: 'text-blue-700', badge: 'text-blue-600' },
      green: { bg: 'bg-green-50', border: 'border-green-500', title: 'text-green-700', badge: 'text-green-600' },
      orange: { bg: 'bg-orange-50', border: 'border-orange-500', title: 'text-orange-700', badge: 'text-orange-600' }
    };
    const classes = colorClasses[color] || colorClasses.purple;

    return (
      <Card className="bg-white/95 backdrop-blur shadow-lg hover:shadow-xl transition-shadow" data-testid={`leaderboard-${metric}`}>
        <CardHeader className={`${classes.bg} border-b-2 ${classes.border} py-3`}>
          <CardTitle className={`${classes.title} text-lg flex items-center gap-2`}>
            <Icon size={20} />
            {title}
          </CardTitle>
          <CardDescription className="text-xs">Top 10 {sortOrder === 'asc' ? 'Fastest' : 'Best'}</CardDescription>
        </CardHeader>
        <CardContent className="pt-3 pb-2">
          <div className="space-y-1">
            {data.length > 0 ? data.map((entry, index) => (
              <div 
                key={`${metric}-${index}`} 
                className={`flex justify-between items-center py-1.5 px-2 rounded text-sm ${index < 3 ? 'bg-gray-50' : ''}`}
              >
                <span className="flex items-center gap-1.5">
                  {index === 0 && <Trophy size={12} className="text-yellow-500" />}
                  {index === 1 && <Trophy size={12} className="text-gray-400" />}
                  {index === 2 && <Trophy size={12} className="text-amber-600" />}
                  <span className={`font-bold ${classes.badge}`}>{index + 1}.</span> 
                  <span className="truncate max-w-[150px]">{entry.athlete_name}</span>
                </span>
                <span className={`font-bold ${classes.title} text-base`}>{entry.value}{unit}</span>
              </div>
            )) : (
              <p className="text-sm text-gray-500 italic text-center py-4">No records yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const AgeLeaderboardCard = ({ age, metric, sortOrder, unit, color }) => {
    const ageData = getLeaderboardByAge(metric, sortOrder);
    const data = ageData[age] || [];
    
    const colorClasses = {
      purple: { bg: 'bg-purple-50', border: 'border-purple-500', title: 'text-purple-700', badge: 'text-purple-600' },
      blue: { bg: 'bg-blue-50', border: 'border-blue-500', title: 'text-blue-700', badge: 'text-blue-600' },
      orange: { bg: 'bg-orange-50', border: 'border-orange-500', title: 'text-orange-700', badge: 'text-orange-600' }
    };
    const classes = colorClasses[color] || colorClasses.purple;

    if (data.length === 0) return null;

    return (
      <Card className="bg-white/95 backdrop-blur shadow-md" data-testid={`age-leaderboard-${age}`}>
        <CardHeader className={`${classes.bg} border-b-2 ${classes.border} py-2`}>
          <CardTitle className={`${classes.title} text-base flex items-center gap-2`}>
            <Users size={16} />
            Age {age}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2 pb-2">
          <div className="space-y-1">
            {data.map((entry, index) => (
              <div 
                key={`age-${age}-${index}`} 
                className={`flex justify-between items-center py-1 px-2 rounded text-sm ${index < 3 ? 'bg-gray-50' : ''}`}
              >
                <span className="flex items-center gap-1.5">
                  {index === 0 && <Trophy size={11} className="text-yellow-500" />}
                  {index === 1 && <Trophy size={11} className="text-gray-400" />}
                  {index === 2 && <Trophy size={11} className="text-amber-600" />}
                  <span className={`font-bold ${classes.badge}`}>{index + 1}.</span> 
                  <span className="truncate max-w-[90px]">{entry.athlete_name}</span>
                </span>
                <span className={`font-bold ${classes.title}`}>{entry.value}{unit}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  const getAgesWithData = (metric, sortOrder) => {
    const ageData = getLeaderboardByAge(metric, sortOrder);
    return Object.keys(ageData).filter(age => ageData[age].length > 0).sort((a, b) => parseInt(a) - parseInt(b));
  };

  const currentMetricConfig = metrics.find(m => m.key === selectedMetric) || metrics[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white text-xl">Loading records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_athlete-dashboard-3/artifacts/tm6xtcdu_496240925_122106843224865340_513732478382055889_n.jpg" 
            alt="The Armory - Strength & Speed" 
            className="h-40 w-auto mb-4"
            data-testid="armory-logo"
          />
          <h1 className="text-3xl font-bold text-white mb-1 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="public-title">
            Top Performers
          </h1>
          <p className="text-base text-white/90">The Armory - Strength & Speed Leaderboards</p>
        </div>
      </div>

      {/* Tabs for Overall vs By Age */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs defaultValue="overall" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-6" data-testid="leaderboard-tabs">
            <TabsTrigger value="overall" data-testid="overall-tab">Overall Top 10</TabsTrigger>
            <TabsTrigger value="byage" data-testid="byage-tab">Top 10 by Age</TabsTrigger>
          </TabsList>

          {/* Overall Top 10 Tab */}
          <TabsContent value="overall">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="leaderboards-grid">
              <LeaderboardCard 
                title="10 Yard Sprint" 
                metric="ten_yard_sprint" 
                sortOrder="asc" 
                color="purple"
                icon={Zap}
                unit="s"
              />
              <LeaderboardCard 
                title="10 Yard Fly" 
                metric="ten_yard_fly" 
                sortOrder="asc" 
                color="purple"
                icon={Zap}
                unit="s"
              />
              <LeaderboardCard 
                title="505 Agility" 
                metric="agility_505" 
                sortOrder="asc" 
                color="orange"
                icon={Target}
                unit="s"
              />
              <LeaderboardCard 
                title="Broad Jump" 
                metric="broad_jump" 
                sortOrder="desc" 
                color="blue"
                icon={Zap}
                unit='"'
              />
              <LeaderboardCard 
                title="Vertical Jump" 
                metric="vertical_jump" 
                sortOrder="desc" 
                color="blue"
                icon={Zap}
                unit='"'
              />
              <AESLeaderboardCard 
                title="AES Score" 
                color="amber"
                icon={Zap}
              />
            </div>
          </TabsContent>

          {/* By Age Tab */}
          <TabsContent value="byage">
            {/* Metric Selector */}
            <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="metric-selector">
              {metrics.map(metric => (
                <Button
                  key={metric.key}
                  variant={selectedMetric === metric.key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedMetric(metric.key)}
                  className={selectedMetric === metric.key ? "bg-slate-700" : "bg-white/90"}
                  data-testid={`metric-btn-${metric.key}`}
                >
                  {metric.label}
                </Button>
              ))}
            </div>

            {/* Age Groups Grid */}
            {currentMetricConfig.isAES ? (
              // AES By Age Display - uses backend data
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="age-leaderboards-grid">
                {Object.entries(aesLeaderboardByAge)
                  .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                  .map(([age, athletes]) => (
                  <Card key={`aes-age-${age}`} className="bg-white/95 backdrop-blur shadow-md" data-testid={`age-leaderboard-${age}`}>
                    <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b-2 border-amber-500 py-2">
                      <CardTitle className="text-amber-700 text-base flex items-center gap-2">
                        <Users size={16} />
                        Age {age}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-2 pb-2">
                      <div className="space-y-1">
                        {athletes.map((entry, index) => (
                          <div 
                            key={`aes-${age}-${index}`} 
                            className={`flex justify-between items-center py-1 px-2 rounded text-sm ${index < 3 ? 'bg-amber-50' : ''}`}
                          >
                            <span className="flex items-center gap-1.5">
                              {index === 0 && <Trophy size={11} className="text-yellow-500" />}
                              {index === 1 && <Trophy size={11} className="text-gray-400" />}
                              {index === 2 && <Trophy size={11} className="text-amber-600" />}
                              <span className="font-bold text-amber-600">{index + 1}.</span> 
                              <span className="truncate max-w-[90px]">{entry.athlete_name}</span>
                            </span>
                            <span className="font-bold text-amber-700">{entry.aes}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              // Regular metrics By Age Display
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="age-leaderboards-grid">
                {getAgesWithData(currentMetricConfig.key, currentMetricConfig.sortOrder).map(age => (
                  <AgeLeaderboardCard
                    key={`age-card-${age}`}
                    age={age}
                    metric={currentMetricConfig.key}
                    sortOrder={currentMetricConfig.sortOrder}
                    unit={currentMetricConfig.unit}
                    color={currentMetricConfig.color}
                  />
                ))}
              </div>
            )}

            {!currentMetricConfig.isAES && getAgesWithData(currentMetricConfig.key, currentMetricConfig.sortOrder).length === 0 && (
              <div className="text-center py-12">
                <p className="text-white/70 text-lg">No records yet for {currentMetricConfig.label}</p>
              </div>
            )}
            {currentMetricConfig.isAES && Object.keys(aesLeaderboardByAge).length === 0 && (
              <div className="text-center py-12">
                <p className="text-white/70 text-lg">No complete AES records yet (requires 10Y Sprint, Broad Jump, and Vertical Jump)</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Contact Information */}
        <div className="mt-8 mb-6 text-center" data-testid="contact-section">
          <div className="bg-white/10 backdrop-blur rounded-lg p-6 max-w-md mx-auto">
            <h3 className="text-xl font-semibold text-white mb-4">Contact Us</h3>
            <div className="space-y-2">
              <p className="text-white/90">
                <span className="font-medium">Email:</span>{' '}
                <a href="mailto:jason@armorytraining.com" className="text-blue-300 hover:text-blue-200 underline">
                  jason@armorytraining.com
                </a>
              </p>
              <p className="text-white/90">
                <span className="font-medium">Text:</span>{' '}
                <a href="sms:801-918-4192" className="text-blue-300 hover:text-blue-200 underline">
                  801-918-4192
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Login Section */}
        <div className="mt-6 mb-6" data-testid="login-section">
          <Card className="max-w-xs mx-auto bg-white/95 backdrop-blur shadow-xl">
            <CardHeader className="text-center border-b py-2">
              <CardTitle className="text-base" data-testid="login-title">Coach & Staff Login</CardTitle>
            </CardHeader>
            <CardContent className="pt-3 pb-3">
              <form onSubmit={handleLogin} className="space-y-3">
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-9 text-center text-sm"
                  autoFocus
                  required
                  data-testid="password-input"
                />
                <Button 
                  type="submit" 
                  className="w-full h-9 text-sm bg-slate-800 hover:bg-slate-700"
                  disabled={loginLoading}
                  data-testid="login-submit-btn"
                >
                  {loginLoading ? "Checking..." : "Login"}
                </Button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowRecovery(true)}
                    className="text-xs text-slate-600 hover:text-slate-900 hover:underline"
                    data-testid="forgot-password-btn"
                  >
                    Forgot password?
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-900 text-white/70 text-center py-4">
        <p className="text-sm">© 2026 The Armory - Strength & Speed</p>
      </div>

      {/* Password Recovery Dialog */}
      <Dialog open={showRecovery} onOpenChange={setShowRecovery}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={20} />
              Password Recovery
            </DialogTitle>
            <DialogDescription>
              A password recovery email will be sent to the registered email address.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>Recovery Email:</strong> kta.jason1@gmail.com
              </p>
              <p className="text-xs text-blue-700 mt-2">
                The password will be sent to this email address.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowRecovery(false)}
              disabled={recoveryLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handlePasswordRecovery}
              disabled={recoveryLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {recoveryLoading ? "Sending..." : "Send Recovery Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicLeaderboard;
